import PropTypes from 'prop-types';

/**
 * ArticleCardProps - Interface compartilhada para todas as variantes do ArticleCard
 *
 * Este arquivo define a ÚNICA interface de props que TODAS as variantes (detailed, minimal, list)
 * devem implementar. Isso garante que:
 *
 * 1. Adicionar nova prop = modificar apenas este arquivo
 * 2. Todas as variantes automaticamente recebem a nova prop
 * 3. Comportamento consistente em todas as variantes
 *
 * TRANSFORMER PATTERN: Um único "ser" que pode se transformar em diferentes formas,
 * mas mantém todas as funcionalidades em todas as formas.
 */

/**
 * PropTypes compartilhados - definição única
 */
export const articleCardPropTypes = {
  // Article data (required)
  article: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.string,
    source: PropTypes.string,
    author: PropTypes.string,
    publishedAt: PropTypes.string,
    url: PropTypes.string,
    siteUrl: PropTypes.string,
    imageUrl: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string,
      textColor: PropTypes.string
    })),
    isFavorite: PropTypes.bool,
    isRead: PropTypes.bool,
    sentiment: PropTypes.string,
    mediaType: PropTypes.string
  }).isRequired,

  // Event handlers - CRUD operations
  onArticleClick: PropTypes.func,
  onToggleFavorite: PropTypes.func,
  onToggleRead: PropTypes.func,
  onUpdateSentiment: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onSendNewsletter: PropTypes.func,

  // Event handlers - Organization
  onAddTag: PropTypes.func,
  onAddToCollection: PropTypes.func,
  onTagClick: PropTypes.func,
  onTagRemove: PropTypes.func,

  // Generic action handler (for menu-based actions)
  onAction: PropTypes.func,

  // Selection mode (bulk operations)
  selectionMode: PropTypes.bool,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,

  // Styling
  className: PropTypes.string,
  hideHoverActions: PropTypes.bool,

  // HOC injected props (withArticleTags)
  renderTags: PropTypes.func
};

/**
 * Default props compartilhados
 */
export const articleCardDefaultProps = {
  selectionMode: false,
  isSelected: false,
  className: '',
  hideHoverActions: false
};

/**
 * TypeScript-like interface documentation (para referência)
 *
 * interface ArticleCardProps {
 *   // Article data
 *   article: Article;
 *
 *   // CRUD operations
 *   onArticleClick?: (article: Article) => void;
 *   onToggleFavorite?: (articleId: string) => void;
 *   onToggleRead?: (articleId: string) => void;
 *   onUpdateSentiment?: (articleId: string, sentiment: string) => void;
 *   onEdit?: (article: Article) => void;
 *   onDelete?: (articleId: string) => void;
 *
 *   // Organization
 *   onAddTag?: (article: Article) => void;
 *   onAddToCollection?: (article: Article) => void;
 *   onTagClick?: (tag: Tag) => void;
 *   onTagRemove?: (tagId: string, article: Article) => void;
 *
 *   // Generic action
 *   onAction?: (actionType: string, article: Article) => void;
 *
 *   // Selection
 *   selectionMode?: boolean;
 *   isSelected?: boolean;
 *   onSelect?: (articleId: string) => void;
 *
 *   // Styling
 *   className?: string;
 *
 *   // HOC
 *   renderTags?: (article: Article) => ReactNode;
 * }
 */
